import { useRef, useState } from 'react'
import { Upload, X, FileText, Image } from 'lucide-react'
import { clsx } from 'clsx'

interface DocumentUploadProps {
  label: string
  accept?: string
  value: File | null
  onChange: (file: File | null) => void
  error?: string
  hint?: string
}

export default function DocumentUpload({
  label,
  accept = 'image/*,.pdf',
  value,
  onChange,
  error,
  hint,
}: DocumentUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleFile = (file: File | undefined) => {
    if (!file) return
    onChange(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleFile(e.dataTransfer.files[0])
  }

  const isImage = value?.type.startsWith('image/')

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-foreground">{label}</label>

      {value ? (
        <div className="flex items-center gap-3 p-3 border border-primary/30 bg-primary/5 rounded-lg">
          {isImage ? <Image size={20} className="text-primary shrink-0" /> : <FileText size={20} className="text-primary shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{value.name}</p>
            <p className="text-xs text-muted-foreground">{(value.size / 1024).toFixed(1)} KB</p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={clsx(
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all',
            dragOver ? 'border-primary bg-primary/5' : error ? 'border-destructive' : 'border-border hover:border-primary/50 hover:bg-muted/50'
          )}
        >
          <Upload size={28} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            Click to upload or drag & drop
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {hint || 'Supported: JPG, PNG, PDF'}
          </p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}
